import {Accordion} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import DataView from 'analysis-tool/data-view/DataView';
import DataViewContext, {DEFAULT_BLOCK_CONFIG} from 'analysis-tool/data-view/DataViewContext';
import {assertAndContinue, assert} from 'Asserts';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import HelpButton from 'help/HelpButton';
import {DEFAULT_BLOCK_HEIGHT} from 'insights/InsightsConstants';
import {FONT_PROXIMA_19, FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import {WHITE} from 'ui-library/Colors';
import * as Colors from 'ui-library/Colors';
import MarkdownContent from 'ui-library/MarkdownContent';
import {assertNever} from 'util/Types';

import ConfigFormBlock from './ConfigFormBlock';
import {useDataLayoutContext} from './DataLayoutContext';
import {GridBlock} from './DataLayoutTypes';

interface GridBlockProps {
  block: GridBlock;
  layoutPath: Array<string | number>;
  hasBorder?: boolean;
}

function BlockContents({block, layoutPath}: GridBlockProps): JSX.Element | null {
  assert(block.type !== 'DATA', 'Data view blocks should be rendered in a DataViewContext.Provider');
  if (block.type === 'CONFIG_FORM') {
    return <ConfigFormBlock block={block} layoutPath={layoutPath} />;
  } else if (block.type === 'MARKDOWN') {
    return <MarkdownContent content={block.markdown} />;
  } else {
    assertAndContinue(false, 'Found unexpected block type in layout', {
      blockType: assertNever(block).type,
    });
    return null;
  }
}

interface GridBlockHeaderProps {
  title?: string;
  subtitle?: string;
  infoText?: string;
}

export function GridBlockHeaderOrNull({title, subtitle, infoText}: GridBlockHeaderProps): JSX.Element | null {
  if (!title && !subtitle) {
    return null;
  }
  assert(title != null || infoText == null, 'infoText cannot be present without a title');

  return (
    <div>
      {title && (
        <div className={css(gridBlockStyles.gridBlockTitleAndInfoText)}>
          {title && <div className={css(gridBlockStyles.gridBlockTitle)}>{title}</div>}
          {infoText && (
            <HelpButton
              renderContent={() => <span className="u-pre-wrap u-normal-font-weight">{infoText}</span>}
              iconClassName="u-mini-spacing-left"
            />
          )}
        </div>
      )}
      {subtitle && <div className={css(gridBlockStyles.gridBlockSubtitle)}>{subtitle}</div>}
    </div>
  );
}

export function GridBlockBody({
  children,
  isCollapsible = false,
  isInLayout,
}: {
  children: React.ReactNode;
  isCollapsible?: boolean;
  isInLayout?: boolean;
}): JSX.Element {
  if (!isCollapsible && !isInLayout) {
    return <>{children}</>;
  }

  if (!isCollapsible) {
    return <div className={css(isInLayout && gridBlockStyles.gridContentHeight)}>{children}</div>;
  }

  return (
    <Accordion
      classNames={{
        panel: css(gridBlockStyles.accordionPanel, isInLayout && gridBlockStyles.gridContentHeight),
        item: css(gridBlockStyles.accordionItem),
        content: css(gridBlockStyles.accordionContent),
        control: css(gridBlockStyles.accordionControl),
      }}
      variant="filled"
    >
      <Accordion.Item value="block">
        <Accordion.Control>Click to show</Accordion.Control>
        <Accordion.Panel>{children}</Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

/** @package */
export default function GridBlock({block, layoutPath, hasBorder}: GridBlockProps): JSX.Element | null {
  const {actionOrigin} = useDataLayoutContext();
  const dataViewContextValue = useMemo(() => {
    if (block.type === 'DATA') {
      return {
        actionOrigin,
        analysisView: block.view,
        blockConfig: block.blockConfig
          ? {...block.blockConfig, isInLayout: true}
          : {...DEFAULT_BLOCK_CONFIG, isInLayout: true},
        dataFrameViewOptions: {
          defaultTableMode: block.tableConfig?.tableMode as DataFrameTableMode | undefined,
        },
      };
    } else {
      return null;
    }
  }, [actionOrigin, block]);

  if (block.type === 'DATA') {
    assert(dataViewContextValue != null, 'Data view context value should be set for DATA blocks');
    return (
      <DataViewContext.Provider value={dataViewContextValue}>
        <div
          className={css(
            gridBlockStyles.fullHeight,
            hasBorder && gridBlockStyles.blockBorder,
            block.blockConfig?.blockMode === 'CONTENT_ONLY' && gridBlockStyles.contentOnlyBlock
          )}
        >
          <DataView />
        </div>
      </DataViewContext.Provider>
    );
  }

  const headerProps =
    block.blockConfig?.blockMode === 'PANE'
      ? {
          title: block.blockConfig?.title ?? undefined,
          subtitle: block.blockConfig?.subtitle ?? undefined,
          infoText: block.blockConfig?.infoText ?? undefined,
        }
      : undefined;

  return (
    <div
      className={css(
        gridBlockStyles.gridBlock,
        gridBlockStyles.fullHeight,
        hasBorder && gridBlockStyles.blockBorder
      )}
    >
      <GridBlockHeaderOrNull {...headerProps} />
      <GridBlockBody isCollapsible={block.blockConfig?.isCollapsible ?? undefined}>
        <BlockContents block={block} layoutPath={layoutPath} />
      </GridBlockBody>
    </div>
  );
}

export const gridBlockStyles = StyleSheet.create({
  gridBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    padding: 10,
    backgroundColor: Colors.WHITE,
    borderRadius: 4,
  },
  blockBorder: {
    border: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  fullHeight: {
    height: '100%',
  },
  gridBlockTitle: {
    ...FONT_PROXIMA_19,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  gridBlockTitleAndInfoText: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
  gridBlockSubtitle: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  accordionContent: {
    height: '100%',
  },
  accordionControl: {
    paddingLeft: 12,
  },
  accordionItem: {
    backgroundColor: WHITE,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  accordionPanel: {
    flexGrow: 1,
  },
  gridContentHeight: {
    // The GridSection sets the max height for the whole block, but we need to set an absolute height
    // for the Chart DATA blocks because we need to pass in an absolute height/width for plotly.
    height: DEFAULT_BLOCK_HEIGHT,
  },
  contentOnlyBlock: {
    backgroundColor: Colors.WHITE,
  },
});
